import { Alert, Divider, InputLabel, Stack } from '@mui/material'
import {
  type $Table,
  type $Top,
  $TopWithDiameter,
  $TopWithSide,
  $TopWithWidthAndLength,
} from 'element-domain/src/__generated/enhanced-model'

import {
  AttributeListSelector,
  ChildEntityCardSelector,
  DistanceDimensionSlider,
} from '@salsita-ai/platform-project-sdk/react'
import { PropertyContainer } from '@salsita-ai/platform-project-sdk/ui'

interface TopPropertyPageProps {
  tableId: $Table['id']
  top: $Top
}

export default function TopPropertyPage({ tableId, top }: TopPropertyPageProps) {
  return (
    <PropertyContainer.Section title="Shape">
      <Divider sx={{ my: 2.5 }} />
      <PropertyContainer.Group spacing="large">
        <PropertyContainer.Item title="Shape">
          <ChildEntityCardSelector entityId={tableId} instanceName="tops" />
        </PropertyContainer.Item>

        <PropertyContainer.Item title="Size">
          {$TopWithWidthAndLength.is(top) && (
            <Stack gap={3}>
              <Stack gap={0.5}>
                <InputLabel>Length</InputLabel>
                <div>
                  <DistanceDimensionSlider key={top.id} entityId={top.id} instanceName="length" step={1} />
                  {top.$violations?.some(v => v.type === 'lengthMightNotBeAvailableForThisTop') && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      Final length depends on lumber availability.
                    </Alert>
                  )}
                </div>
              </Stack>
              <Stack gap={0.5}>
                <InputLabel>Width</InputLabel>
                <div>
                  <DistanceDimensionSlider key={top.id} entityId={top.id} instanceName="width" step={1} />
                  {top.$violations?.some(v => v.type === 'widthMaybeTooBigForDoor') && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      Width might be too big for a standard doorway.
                    </Alert>
                  )}
                </div>
              </Stack>
            </Stack>
          )}
          {$TopWithSide.is(top) && (
            <Stack gap={0.5}>
              <InputLabel>Side</InputLabel>
              <div>
                <DistanceDimensionSlider key={top.id} entityId={top.id} instanceName="side" step={1} />
                {top.$violations?.some(v => v.type === 'widthMaybeTooBigForDoor') && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Width might be too big for a standard doorway.
                  </Alert>
                )}
              </div>
            </Stack>
          )}
          {$TopWithDiameter.is(top) && (
            <Stack gap={0.5}>
              <InputLabel>Diameter</InputLabel>
              <div>
                <DistanceDimensionSlider key={top.id} entityId={top.id} instanceName="diameter" step={1} />
                {top.$violations?.some(v => v.type === 'widthMaybeTooBigForDoor') && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Width might be too big for a standard doorway.
                  </Alert>
                )}
              </div>
            </Stack>
          )}
        </PropertyContainer.Item>

        <PropertyContainer.Item title="Thickness">
          <Stack gap={4}>
            <div>
              <AttributeListSelector entityId={top.id} instanceName="thicknessType" />
              {top.$violations?.some(v => v.type === 'thicknessTypeMightNotBeAvailableForThisTop') && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Final thickness depends on lumber availability.
                </Alert>
              )}
            </div>
            <Stack gap={0.5}>
              <InputLabel>Thickness</InputLabel>
              <DistanceDimensionSlider key={top.id} entityId={top.id} instanceName="thickness" step={0.25} />
            </Stack>
          </Stack>
        </PropertyContainer.Item>
      </PropertyContainer.Group>
    </PropertyContainer.Section>
  )
}
